import { useCallback, useEffect, useRef, useState } from "react";
import { openConfirmModal } from "@mantine/modals";
import { showNotification } from "@mantine/notifications";
import { IconAlertCircle, IconCheck } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import {
  canSubmitMcaCreateLocally,
  getMcaSubmitDisabledReason,
  isMcaDraftStatus,
  mcaSubmitFailureMessage,
  resolveMcaEntityKey,
  shouldShowMcaSubmit,
} from "../helpers/mcaSubmitHelpers";
import {
  getMcaSubmittedNotificationMessage,
  getMcaSubmittedNotificationTitle,
  isMcaIntercepted,
} from "../components/mca/mcaApprovalLabels";

const normalizeMcaRequestId = (mcaRequestId) => {
  if (!mcaRequestId) return "";
  if (typeof mcaRequestId === "object") {
    return String(mcaRequestId._id ?? "");
  }
  return String(mcaRequestId);
};

export const useMcaSubmitAction = ({
  api,
  apiEntity,
  mcaEntity,
  formMode,
  entity,
  isDirty,
  onSubmitted,
}) => {
  const { t } = useTranslation();
  const apiRef = useRef(api);
  apiRef.current = api;

  const onSubmittedRef = useRef(onSubmitted);
  onSubmittedRef.current = onSubmitted;

  const [matchCreate, setMatchCreate] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const lastFetchedKeyRef = useRef(null);

  const localCheck = canSubmitMcaCreateLocally({
    apiEntity,
    formMode,
    entity,
    isDirty,
  });
  const targetEntity = resolveMcaEntityKey(apiEntity, mcaEntity);

  const entityId = entity?._id;
  const matchFetchKey = [
    targetEntity,
    entityId,
    entity?.status,
    entity?.mcaPending ? "1" : "0",
    normalizeMcaRequestId(entity?.mcaRequestId),
    formMode,
    localCheck.eligible ? "1" : "0",
  ].join(":");

  useEffect(() => {
    let cancelled = false;

    if (!localCheck.eligible || !entityId) {
      lastFetchedKeyRef.current = null;
      setMatchCreate(null);
      setMatchLoading(false);
      return;
    }

    const isNewFetch = lastFetchedKeyRef.current !== matchFetchKey;
    if (isNewFetch) {
      lastFetchedKeyRef.current = matchFetchKey;
      setMatchLoading(true);
    }

    const loadMatch = async () => {
      try {
        const result = await apiRef.current.Mca.matchCreate(
          targetEntity,
          entityId
        );
        if (!cancelled) {
          setMatchCreate(result?.data ?? null);
        }
      } catch {
        if (!cancelled) {
          setMatchCreate(null);
        }
      } finally {
        if (!cancelled) {
          setMatchLoading(false);
        }
      }
    };

    loadMatch();
    return () => {
      cancelled = true;
    };
  }, [entityId, formMode, localCheck.eligible, matchFetchKey, targetEntity]);

  const canSubmitMcaCreate =
    localCheck.eligible &&
    !localCheck.submitDisabled &&
    !!matchCreate?.match &&
    !(matchLoading && !matchCreate);

  const mcaSubmitDisabledReason = getMcaSubmitDisabledReason({
    localCheck,
    matchCreate,
    matchLoading,
    submitBlockReason: entity?.submitBlockReason,
    t,
  });

  const submitForApproval = useCallback(async () => {
    if (!canSubmitMcaCreate || !entityId) return { success: false };

    return new Promise((resolve) => {
      openConfirmModal({
        title: t("Submit for approval"),
        children: t(
          "Submit this record for MCA approval? You will not be able to edit it until the request is resolved."
        ),
        labels: { confirm: t("Submit"), cancel: t("Cancel") },
        confirmProps: { color: "green" },
        onCancel: () => resolve({ success: false, cancelled: true }),
        onConfirm: async () => {
          setSubmitting(true);
          try {
            const result = await apiRef.current.Mca.submitEntity({
              targetEntity,
              targetEntityId: entityId,
            });
            if (!isMcaIntercepted(result)) {
              throw new Error(
                mcaSubmitFailureMessage(result, t("Failed to submit for approval")),
              );
            }
            showNotification({
              color: "orange",
              icon: <IconCheck size={18} />,
              title: getMcaSubmittedNotificationTitle(),
              message: getMcaSubmittedNotificationMessage(result),
            });
            if (onSubmittedRef.current) await onSubmittedRef.current(result);
            resolve({ success: true, result });
          } catch (error) {
            showNotification({
              color: "red",
              icon: <IconAlertCircle size={18} />,
              title: t("Error"),
              message: error?.message ?? t("Failed to submit for approval"),
            });
            resolve({ success: false, error });
          } finally {
            setSubmitting(false);
          }
        },
      });
    });
  }, [canSubmitMcaCreate, entityId, t, targetEntity]);

  return {
    canSubmitMcaCreate,
    mcaSubmitDisabledReason,
    submitForApproval,
    submitting,
    matchCreate,
    showMcaSubmit: shouldShowMcaSubmit({ localCheck, matchCreate, matchLoading }),
    isMcaDraftStatus: isMcaDraftStatus(entity?.status),
  };
};
